import { defineChatPlace } from '../../../chat/panel';
import { boardChat } from '../boardChat';
import type { HeatmapPayload, PlannerDashboardPayload, SimulatorPayload } from '../../../types/plannerDashboard';

/**
 * A planner board opens as three pages (PlannerDashboardPage), and each has a
 * chat of its own: its own welcome, starters and placeholder here, its own
 * briefing and tool on the server (planner_chat.py), keyed by the `view` the
 * page sends. A page must never introduce itself as another one.
 */

/** The chat docked beside a planner board: the board's place, with what-if starters. */
export const plannerChat = defineChatPlace({
  ...boardChat,
  welcome: { title: '', subtitle: 'Ask what-ifs about agents, leads and campaigns.' },
  starters: [
    'How many agents can we run on Monday and still hit the target?',
    'What if each agent gets 20 leads instead?',
    'Which campaigns pull Wednesday under the target?',
    'Does adding Forbes leads help us hit the target?',
  ],
  placeholder: 'Ask about this plan…',
});

/** What the agent is told is on screen: the plan and each day's result, not every campaign. */
export function buildPlannerSurfaceContext(dashboardId: string, payload: PlannerDashboardPayload): Record<string, unknown> {
  const plan = Object.fromEntries(payload.inputs.map((i) => [i.key, i.value]));
  return {
    type: 'manual_dashboard',
    kind: 'planner',
    dashboard_id: dashboardId,
    name: payload.spec.name,
    plan,
    channels: payload.filters.find((f) => f.key === 'channel')?.selected ?? [],
    range: { start: payload.range.used_start, end: payload.range.used_end },
    summary: payload.summary,
    days: payload.days.map((d) => ({
      day: d.name,
      leads_needed: d.leads_needed,
      leads_coming_in: Math.round(d.leads_available),
      short_by: Math.round(d.short_by),
      conversion: d.conversion === null ? null : Math.round(d.conversion * 10) / 10,
      hits_target: d.hits_target,
      most_agents: d.most_agents,
    })),
    as_of: payload.meta.as_of,
  };
}

/** Beside Campaign Performance: campaigns by day of the week, nothing about staffing. */
export const campaignsChat = defineChatPlace({
  ...boardChat,
  welcome: { title: '', subtitle: 'Ask which campaigns move leads along, and on which days.' },
  starters: [
    'Which campaigns convert best on Tuesdays?',
    'Which campaigns get leads contacted but rarely to CAS?',
    'How does Facebook compare with Google from MQL to CAS?',
    'Which campaign falls off most at the weekend?',
  ],
  placeholder: 'Ask about these campaigns…',
});

/** Beside the Cost / Submit Simulator: targets, paths and what each would take. */
export const simulatorChat = defineChatPlace({
  ...boardChat,
  welcome: { title: '', subtitle: 'Ask what a submit would have cost, or what it would take to reach one.' },
  starters: [
    'What would a submit have cost at 20% contact?',
    'What if a lead may cost up to $80?',
    'Why can moving spend alone not reach the target?',
    'What contact rate would $1,200 a submit need on its own?',
  ],
  placeholder: 'Ask about cost per submit…',
});

type Base = { dashboardId: string; title: string; channels: string[]; range: { start: string; end: string } };

/** What the agent is told is on Campaign Performance: the steps, and the campaigns as drawn. */
export function buildCampaignsSurfaceContext(
  base: Base,
  grid: HeatmapPayload | null,
  /** The campaign whose hour-by-hour window is open, if one is. */
  openCampaign: { channel: string; campaign: string } | null = null,
): Record<string, unknown> {
  const label = (key: string | undefined) => grid?.stages.find((s) => s.key === key)?.label ?? key;
  return {
    type: 'manual_dashboard',
    kind: 'planner',
    view: 'campaigns',
    dashboard_id: base.dashboardId,
    title: base.title,
    channels: base.channels,
    range: grid?.range ?? base.range,
    from_stage: grid?.from,
    to_stage: grid?.to,
    steps: grid ? `${label(grid.from)} to ${label(grid.to)}` : null,
    min_leads: grid?.min_leads,
    // Days of the week, hours of the day or cohorts of the range: the chat
    // reads the grid the same way.
    by: grid?.by,
    // The clock the hours read on, and the zone the chat's tools read them in.
    clock: grid?.hours?.label ?? null,
    zone: grid?.hours?.label ?? null,
    campaign_open: openCampaign,
    totals: grid?.totals,
    // How many, not which: the chat reads the campaigns themselves through
    // `read_campaign_heatmap`, so they are never sent with every question.
    campaigns_shown: grid?.rows.length ?? 0,
    hidden_campaigns: grid?.hidden_campaigns ?? 0,
  };
}

/** What the agent is told is on the Cost / Submit Simulator: the targets as last run, and each path. */
export function buildSimulatorSurfaceContext(
  base: Base,
  sim: SimulatorPayload | null,
  calculator?: { contact: number; conversion: number; cost_per_submit: number | null } | null,
): Record<string, unknown> {
  const figures = (r: SimulatorPayload['today']) => ({
    cost_per_submit: r.cost_per_submit,
    cost_per_lead: r.cost_per_lead,
    spend_day: r.spend_day,
    leads_day: r.leads_day,
    submits_day: r.submits_day,
    contact: r.contact,
    conversion: r.conversion,
    agents: r.agents,
  });
  return {
    type: 'manual_dashboard',
    kind: 'planner',
    view: 'simulator',
    dashboard_id: base.dashboardId,
    title: base.title,
    channels: base.channels,
    range: base.range,
    goal: sim?.goal ?? null,
    today: sim ? figures(sim.today) : null,
    // The calculator's rates as last calculated (percent) and what they give.
    calculator: calculator ?? null,
    // The eight paths are the page's headline, not a list: which reach the
    // target, at what cost. Each path's campaigns are left to the tool.
    paths: (sim?.paths ?? []).map((p) => ({
      path: p.label,
      reaches_target: p.reaches_goal,
      what_it_takes: p.summary,
      ...figures(p.result),
    })),
  };
}
